import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Pencil, Plus, UserMinus } from "lucide-react";
import {
  ApiError,
  createAdminTeam,
  getAdminTeamLiveStatuses,
  getAdminTeamMemberships,
  getAdminTeams,
  getAdminUsers,
  grantAdminTeamMembership,
  revokeAdminTeamMembership,
  updateAdminTeam,
} from "@/lib/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useToast } from "@/components/ui/toast";
import { TkticSelect } from "@/components/ui/TkticSelect";
import {
  LiveStatusPill,
  deriveLiveStatusPillState,
} from "@/features/live/components/LiveStatusPill";
import { unscopedKey } from "@/lib/queryClient";
import { slugify } from "@/lib/slug";
import type {
  AdminTeamMembership,
  AdminTeamRow,
  AdminUser,
} from "@/lib/types";
import { AdminIconButton } from "@/components/console/AdminIconButton";
import { AdminMetricTile } from "@/components/console/AdminMetricTile";
import { AdminPageHeader } from "@/components/console/AdminPageHeader";

/**
 * M8b F1–F9 — `/admin/teams` (global-admin club squads).
 *
 * Two-pane shell:
 *   - Left: squad list with filter input + new-team button.
 *   - Right: detail panel with two sub-tabs (Overview / Access).
 *     Selected team id lives in URL search so refresh keeps it.
 *
 * Seasons are globally shared and managed on the standalone
 * `/admin/seasons` page — they are no longer a team sub-resource.
 *
 * The F7 memberships sub-tab handles the 409 last-admin response — a
 * parity improvement vs the legacy which surfaced a generic error.
 */

const TEAM_ROLE_ORDER: Record<string, number> = {
  team_admin: 0,
  coach: 1,
  assistant_coach: 2,
  player: 3,
  guardian: 4,
};

const GRANT_ROLES = [
  { value: "team_admin", label: "Team admin" },
  { value: "coach", label: "Coach" },
  { value: "assistant_coach", label: "Assistant coach" },
  { value: "player", label: "Player" },
  { value: "guardian", label: "Guardian" },
];

const GAME_FORMATS = ["full", "7v7", "9v9", "11v11"];

type DetailTab = "overview" | "memberships";

export function AdminTeamsPage() {
  const teamsQuery = useQuery({
    queryKey: unscopedKey("admin-teams"),
    queryFn: getAdminTeams,
  });
  const teams = teamsQuery.data ?? [];

  // Aggregate live-status poll — one MediaMTX call hydrates every
  // team's pill. Background-paused so the page doesn't pull a
  // worker thread when the tab is hidden.
  const teamLiveStatusesQuery = useQuery({
    queryKey: unscopedKey("admin-team-statuses"),
    queryFn: getAdminTeamLiveStatuses,
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });
  const teamLiveByTeamId = useMemo(() => {
    const map = new Map<string, { active: boolean; ready: boolean }>();
    for (const entry of teamLiveStatusesQuery.data?.teams ?? []) {
      map.set(entry.team_id, {
        active: entry.active,
        ready: entry.ready,
      });
    }
    return map;
  }, [teamLiveStatusesQuery.data]);
  const teamLiveReachable = teamLiveStatusesQuery.data?.reachable;
  const liveSignals = useMemo(() => {
    const liveRows = Array.from(teamLiveByTeamId.values());
    return {
      publicCount: teams.filter((team) => team.public).length,
      formatCount: new Set(teams.map((team) => team.game_format)).size,
      ready: liveRows.filter((row) => row.ready).length,
      active: liveRows.filter((row) => row.active).length,
    };
  }, [teamLiveByTeamId, teams]);

  // Re-review P2: selected team lives in the URL (`?team=`) so a
  // refresh / deep-link / back-nav restores the operator's pick.
  // The page-local cache is purely a hint: when the search param
  // is present, it wins; otherwise we auto-pick the first team
  // and write that choice back to the URL.
  const search = useSearch({ from: "/admin/teams" });
  const navigate = useNavigate({ from: "/admin/teams" });
  const selectedTeamId: string | null = search.team ?? null;
  const setSelectedTeamId = (id: string | null) => {
    void navigate({
      search: (prev) => ({ ...prev, team: id ?? undefined }),
      replace: false,
    });
  };

  const [filter, setFilter] = useState("");
  const [tab, setTab] = useState<DetailTab>("overview");
  const [newTeamOpen, setNewTeamOpen] = useState(false);

  // Auto-pick first team only when the URL has no `?team=` AND
  // there are teams to choose from. Writes the choice back to the
  // URL so it survives a refresh, AND so the operator sees the
  // canonical URL in their address bar.
  useEffect(() => {
    if (selectedTeamId == null && teams.length > 0) {
      void navigate({
        search: (prev) => ({ ...prev, team: teams[0]!.id }),
        replace: true,
      });
    }
  }, [teams, selectedTeamId, navigate]);

  // If the URL points at a team that no longer exists (deleted on
  // another tab, or stale bookmark), fall back to the first team
  // rather than rendering the empty-state forever.
  useEffect(() => {
    if (
      selectedTeamId != null &&
      teams.length > 0 &&
      !teams.some((t) => t.id === selectedTeamId)
    ) {
      void navigate({
        search: (prev) => ({ ...prev, team: teams[0]!.id }),
        replace: true,
      });
    }
  }, [teams, selectedTeamId, navigate]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return teams;
    return teams.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        t.slug.toLowerCase().includes(q),
    );
  }, [teams, filter]);

  const selected = teams.find((t) => t.id === selectedTeamId) ?? null;

  return (
    <div data-testid="admin-teams" className="flex flex-col gap-3">
      <AdminPageHeader
        eyebrow="Club setup"
        title="Squads"
        description="Manage the squads inside this club, their default formats, broadcast readiness, and account access."
        meta={`${teams.length} ${teams.length === 1 ? "squad" : "squads"}`}
      />

      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-5">
        <AdminMetricTile
          label="Club squads"
          value={String(teams.length)}
          sub="Squads available to coaches"
          testid="admin-teams-signal-total"
        />
        <AdminMetricTile
          label="Public pages"
          value={`${liveSignals.publicCount}/${teams.length}`}
          sub="Visible on the public team index"
          tone={liveSignals.publicCount > 0 ? "good" : "neutral"}
          testid="admin-teams-signal-public"
        />
        <AdminMetricTile
          label="Game formats"
          value={String(liveSignals.formatCount)}
          sub="Distinct formats in use"
          tone={liveSignals.formatCount > 0 ? "good" : "neutral"}
          testid="admin-teams-signal-formats"
        />
        <AdminMetricTile
          label="Live ready"
          value={`${liveSignals.ready}/${teams.length}`}
          sub={teamLiveReachable === false ? "Media server unreachable" : "Media server can see stream keys"}
          tone={teamLiveReachable === false ? "warn" : liveSignals.ready > 0 ? "good" : "neutral"}
          testid="admin-teams-signal-live-ready"
        />
        <AdminMetricTile
          label="On air"
          value={String(liveSignals.active)}
          sub="Active broadcast sessions"
          tone={liveSignals.active > 0 ? "busy" : "neutral"}
          testid="admin-teams-signal-active"
        />
      </div>

      {teamsQuery.error ? (
        <p
          data-testid="admin-teams-error"
          className="text-destructive font-mono text-xs"
        >
          Failed to load squads.
        </p>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,260px)_1fr] gap-3">
        {/* Left pane — team list */}
        <aside
          data-testid="admin-teams-list-pane"
          className="rounded border border-rule bg-surface shadow-sm p-2 flex flex-col gap-2 h-fit"
        >
          <div className="flex items-center gap-1">
            <input
              type="search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter…"
              data-testid="admin-teams-filter"
              className="grow rounded border border-rule bg-surface px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-accent"
            />
            <AdminIconButton
              icon={Plus}
              label="New squad"
              tone="accent"
              onClick={() => setNewTeamOpen(true)}
              data-testid="admin-teams-new"
            />
          </div>
          {filtered.length === 0 ? (
            <p
              data-testid="admin-teams-empty"
              className="text-xs font-mono text-ink-muted px-1 py-2"
            >
              {teams.length === 0 ? "No squads yet." : "No squads match the filter."}
            </p>
          ) : (
            <ul className="flex flex-col gap-0.5">
              {filtered.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedTeamId(t.id)}
                    data-testid={`admin-teams-row-${t.id}`}
                    data-active={t.id === selectedTeamId ? "true" : undefined}
                    className="w-full text-left rounded border border-transparent hover:border-rule hover:bg-field data-[active=true]:border-rule data-[active=true]:bg-surface px-2 py-1.5 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <div className="text-ink text-sm truncate flex-1 min-w-0">
                        {t.name}
                      </div>
                      <PublicPill isPublic={!!t.public} />
                      <LiveStatusPill
                        state={deriveLiveStatusPillState(
                          teamLiveReachable,
                          teamLiveByTeamId.get(t.id),
                        )}
                        className="shrink-0"
                      />
                    </div>
                    <div className="text-[0.6rem] font-mono text-ink-muted truncate">
                      {t.slug}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* Right pane — detail */}
        <section
          data-testid="admin-teams-detail-pane"
          className="rounded border border-rule bg-surface shadow-sm p-3 min-h-[280px]"
        >
          {!selected ? (
            <p
              data-testid="admin-teams-detail-empty"
              className="text-xs font-mono text-ink-muted"
            >
              Select a squad to view its setup and access.
            </p>
          ) : (
            <TeamDetail
              team={selected}
              tab={tab}
              setTab={setTab}
            />
          )}
        </section>
      </div>

      {newTeamOpen ? (
        <NewTeamModal
          onClose={() => setNewTeamOpen(false)}
          onCreated={(team) => {
            setSelectedTeamId(team.id);
            setNewTeamOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail pane — Overview / Access sub-tabs
// ---------------------------------------------------------------------------

function TeamDetail({
  team,
  tab,
  setTab,
}: {
  team: AdminTeamRow;
  tab: DetailTab;
  setTab: (t: DetailTab) => void;
}) {
  const queryClient = useQueryClient();
  const membershipsQuery = useQuery({
    queryKey: unscopedKey("admin-teams-memberships", { teamId: team.id }),
    queryFn: () => getAdminTeamMemberships(team.id),
  });

  const [renameOpen, setRenameOpen] = useState(false);

  return (
    <div className="flex flex-col gap-3">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="min-w-0">
          <h3
            data-testid="admin-teams-detail-name"
            className="text-base font-display text-ink truncate"
          >
            {team.name}
          </h3>
          <div className="text-[0.6rem] font-mono text-ink-muted truncate">
            {team.slug} · {team.game_format} · created {team.created_at}
          </div>
        </div>
        <AdminIconButton
          icon={Pencil}
          label="Edit squad"
          onClick={() => setRenameOpen(true)}
          data-testid="admin-teams-detail-rename"
        />
      </header>

      <nav
        data-testid="admin-teams-detail-tabs"
        role="tablist"
        className="inline-flex gap-1 border-b border-rule"
      >
        <DetailTabButton
          active={tab === "overview"}
          onClick={() => setTab("overview")}
          testid="admin-teams-tab-overview"
        >
          Overview
        </DetailTabButton>
        <DetailTabButton
          active={tab === "memberships"}
          onClick={() => setTab("memberships")}
          testid="admin-teams-tab-memberships"
        >
          Access
        </DetailTabButton>
      </nav>

      {tab === "overview" ? (
        <OverviewSubtab
          team={team}
          membershipsCount={membershipsQuery.data?.length ?? 0}
        />
      ) : null}
      {tab === "memberships" ? (
        <MembershipsSubtab
          team={team}
          memberships={membershipsQuery.data ?? []}
          loading={membershipsQuery.isLoading}
          onMutated={() => {
            void queryClient.invalidateQueries({
              queryKey: unscopedKey("admin-teams-memberships", {
                teamId: team.id,
              }),
            });
          }}
        />
      ) : null}

      {renameOpen ? (
        <EditTeamModal
          team={team}
          onClose={() => setRenameOpen(false)}
          onSaved={() => {
            void queryClient.invalidateQueries({
              queryKey: unscopedKey("admin-teams"),
            });
            setRenameOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

function DetailTabButton({
  active,
  onClick,
  testid,
  children,
}: {
  active: boolean;
  onClick: () => void;
  testid: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      data-testid={testid}
      data-active={active ? "true" : undefined}
      className="px-3 py-1.5 text-xs font-display uppercase tracking-wider text-ink-muted hover:text-ink data-[active=true]:text-ink data-[active=true]:border-b-2 data-[active=true]:border-accent transition-colors"
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// F3 / F8 — Overview sub-tab
// ---------------------------------------------------------------------------

function OverviewSubtab({
  team,
  membershipsCount,
}: {
  team: AdminTeamRow;
  membershipsCount: number;
}) {
  return (
    <div
      data-testid="admin-teams-overview"
      className="grid grid-cols-2 lg:grid-cols-4 gap-2"
    >
      <Kpi
        label="Access grants"
        value={String(membershipsCount)}
        testid="admin-teams-kpi-memberships"
      />
      <Kpi
        label="Default format"
        value={team.game_format}
        testid="admin-teams-kpi-format"
      />
      <Kpi
        label="Public page"
        value={team.public ? "Published" : "Private"}
        testid="admin-teams-kpi-public"
      />
      <Kpi
        label="Created"
        value={team.created_at}
        testid="admin-teams-kpi-created"
      />
    </div>
  );
}

function Kpi({
  label,
  value,
  testid,
}: {
  label: string;
  value: string;
  testid: string;
}) {
  return (
    <div
      data-testid={testid}
      className="flex flex-col rounded border border-rule bg-elevated p-2"
    >
      <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
        {label}
      </span>
      <span className="text-sm font-mono text-ink">{value}</span>
    </div>
  );
}

function PublicPill({ isPublic }: { isPublic: boolean }) {
  return (
    <span
      data-testid="admin-teams-public-pill"
      data-public={isPublic ? "true" : "false"}
      className="shrink-0 rounded-full border border-rule bg-surface px-2 py-0.5 text-[0.55rem] font-display uppercase tracking-wider text-ink-muted data-[public=true]:border-good/40 data-[public=true]:text-good"
    >
      {isPublic ? "Public" : "Private"}
    </span>
  );
}

// ---------------------------------------------------------------------------
// F7 — Memberships sub-tab (with the M8b 409 last-admin parity-improvement)
// ---------------------------------------------------------------------------

function MembershipsSubtab({
  team,
  memberships,
  loading,
  onMutated,
}: {
  team: AdminTeamRow;
  memberships: AdminTeamMembership[];
  loading: boolean;
  onMutated: () => void;
}) {
  const [grantOpen, setGrantOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] =
    useState<AdminTeamMembership | null>(null);

  const sorted = useMemo(
    () =>
      [...memberships].sort((a, b) => {
        const ar = TEAM_ROLE_ORDER[a.role] ?? 99;
        const br = TEAM_ROLE_ORDER[b.role] ?? 99;
        if (ar !== br) return ar - br;
        return (a.username ?? "").localeCompare(b.username ?? "");
      }),
    [memberships],
  );

  return (
    <section
      data-testid="admin-teams-memberships"
      className="flex flex-col gap-2"
    >
      <header className="flex items-baseline justify-between">
        <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
          {memberships.length} member{memberships.length === 1 ? "" : "s"}
        </span>
        <AdminIconButton
          icon={Plus}
          label="Grant membership"
          tone="accent"
          onClick={() => setGrantOpen(true)}
          data-testid="admin-teams-memberships-new"
        />
      </header>

      {loading ? (
        <p className="text-xs font-mono text-ink-muted">Loading…</p>
      ) : sorted.length === 0 ? (
        <p
          data-testid="admin-teams-memberships-empty"
          className="text-xs font-mono text-ink-muted"
        >
          No memberships yet.
        </p>
      ) : (
        <ul className="flex flex-col gap-1">
          {sorted.map((m) => (
            <li
              key={m.id}
              data-testid="admin-teams-membership-row"
              data-membership-id={m.id}
              data-role={m.role}
              className="grid grid-cols-[1fr_auto_auto] gap-2 items-center rounded border border-rule/60 bg-elevated px-2 py-1.5 text-xs"
            >
              <div className="min-w-0">
                <div className="text-ink truncate">
                  {m.display_name || m.username}{" "}
                  <span className="text-ink-muted">@{m.username}</span>
                </div>
              </div>
              <span
                data-role={m.role}
                className="rounded border bg-surface px-1.5 py-0.5 text-[0.55rem] font-display uppercase tracking-wider border-rule text-ink-muted"
              >
                {m.role.replace("_", " ")}
              </span>
              <AdminIconButton
                icon={UserMinus}
                label="Revoke membership"
                onClick={() => setRevokeTarget(m)}
                data-testid="admin-teams-membership-revoke"
                tone="destructive"
              />
            </li>
          ))}
        </ul>
      )}

      {grantOpen ? (
        <GrantMembershipModal
          team={team}
          onClose={() => setGrantOpen(false)}
          onGranted={() => {
            setGrantOpen(false);
            onMutated();
          }}
        />
      ) : null}

      {revokeTarget ? (
        <RevokeMembershipConfirm
          team={team}
          membership={revokeTarget}
          onClose={() => setRevokeTarget(null)}
          onRevoked={() => {
            setRevokeTarget(null);
            onMutated();
          }}
        />
      ) : null}
    </section>
  );
}

function GrantMembershipModal({
  team,
  onClose,
  onGranted,
}: {
  team: AdminTeamRow;
  onClose: () => void;
  onGranted: () => void;
}) {
  // Users picker fetched lazily on mount; cached at the page level
  // by TanStack so revisiting the modal in the same session reuses.
  const usersQuery = useQuery({
    queryKey: unscopedKey("admin-users"),
    queryFn: getAdminUsers,
  });
  const toast = useToast();
  const [filter, setFilter] = useState("");
  const [userId, setUserId] = useState<string>("");
  const [role, setRole] = useState("coach");
  const [error, setError] = useState<string | null>(null);

  const filteredUsers = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const users = usersQuery.data ?? [];
    if (!q) return users.slice(0, 50);
    return users
      .filter(
        (u) =>
          u.username.toLowerCase().includes(q) ||
          u.display_name?.toLowerCase().includes(q),
      )
      .slice(0, 50);
  }, [usersQuery.data, filter]);

  const mut = useMutation({
    mutationFn: () =>
      grantAdminTeamMembership(team.id, { user_id: userId, role }),
    onSuccess: () => {
      toast.success("Membership granted");
      onGranted();
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error("Couldn't grant membership", {
        description: formatErr(err),
      });
    },
  });

  return (
    <ConfirmDialog
      title="Grant membership"
      message={
        <div className="flex flex-col gap-2">
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
              Filter users
            </span>
            <input
              type="search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Username or display name"
              data-testid="admin-teams-grant-user-filter"
              className="rounded border border-rule bg-surface px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </label>
          <div
            data-testid="admin-teams-grant-user-list"
            className="flex flex-col gap-0.5 max-h-40 overflow-auto rounded border border-rule bg-elevated p-1"
          >
            {usersQuery.isLoading ? (
              <p className="text-[0.6rem] font-mono text-ink-muted px-1">
                Loading users…
              </p>
            ) : filteredUsers.length === 0 ? (
              <p className="text-[0.6rem] font-mono text-ink-muted px-1">
                No matching users.
              </p>
            ) : (
              filteredUsers.map((u: AdminUser) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => setUserId(u.id)}
                  data-testid={`admin-teams-grant-user-${u.id}`}
                  data-active={userId === u.id ? "true" : undefined}
                  className="flex items-center gap-1 w-full rounded px-1 py-0.5 text-xs text-left transition-colors hover:bg-field data-[active=true]:text-accent"
                >
                  <span className="text-ink">
                    {u.display_name || u.username}{" "}
                    <span className="text-ink-muted">@{u.username}</span>
                  </span>
                  {userId === u.id ? <span className="ml-auto text-accent">✓</span> : null}
                </button>
              ))
            )}
          </div>

          <div
            role="group"
            aria-label="Role"
            data-testid="admin-teams-grant-role"
            className="flex flex-col gap-1"
          >
            <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
              Role
            </span>
            <div className="flex flex-wrap gap-1">
              {GRANT_ROLES.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setRole(r.value)}
                  data-testid={`admin-teams-grant-role-${r.value}`}
                  data-active={role === r.value ? "true" : undefined}
                  className="rounded border px-3 py-1 text-xs font-display uppercase tracking-wider transition-colors border-rule text-ink-muted hover:text-ink hover:bg-field data-[active=true]:border-accent data-[active=true]:bg-accent data-[active=true]:text-accent-ink"
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          {error ? (
            <p
              data-testid="admin-teams-grant-error"
              className="text-destructive font-mono text-xs"
            >
              {error}
            </p>
          ) : null}
        </div>
      }
      confirmLabel={mut.isPending ? "Granting…" : "Grant"}
      onConfirm={() => {
        setError(null);
        if (!userId) {
          setError("Pick a user.");
          return;
        }
        mut.mutate();
      }}
      onClose={onClose}
      data-testid="admin-teams-grant-modal"
    />
  );
}

function RevokeMembershipConfirm({
  team,
  membership,
  onClose,
  onRevoked,
}: {
  team: AdminTeamRow;
  membership: AdminTeamMembership;
  onClose: () => void;
  onRevoked: () => void;
}) {
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: () =>
      revokeAdminTeamMembership(team.id, membership.id),
    onSuccess: () => {
      toast.success("Membership revoked");
      onRevoked();
    },
    // F7 parity improvement: surface 409 last-admin inline (legacy
    // showed a generic error). The backend's 409 detail string
    // already explains it; we just keep it as-is when present.
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        setError(
          typeof err.detail === "string"
            ? err.detail
            : "A team must keep at least one team admin.",
        );
      } else {
        setError(formatErr(err));
      }
      toast.error("Couldn't revoke membership", {
        description: formatErr(err),
      });
    },
  });
  return (
    <ConfirmDialog
      title="Revoke membership?"
      message={
        <>
          <p>
            <strong>{membership.display_name || membership.username}</strong>{" "}
            (@{membership.username}) loses their{" "}
            <strong>{membership.role.replace("_", " ")}</strong> role on this
            team.
          </p>
          {error ? (
            <p
              data-testid="admin-teams-revoke-error"
              className="mt-2 text-destructive font-mono text-xs"
            >
              {error}
            </p>
          ) : null}
        </>
      }
      confirmLabel={mut.isPending ? "Revoking…" : "Revoke"}
      danger
      onConfirm={() => {
        setError(null);
        mut.mutate();
      }}
      onClose={onClose}
      data-testid="admin-teams-revoke-confirm"
    />
  );
}

// ---------------------------------------------------------------------------
// F4 + F5 — New team modal + Edit team modal
// ---------------------------------------------------------------------------

function NewTeamModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (team: AdminTeamRow) => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const [gameFormat, setGameFormat] = useState("full");
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      createAdminTeam({ name, slug, game_format: gameFormat }),
    onSuccess: (team) => {
      void queryClient.invalidateQueries({
        queryKey: unscopedKey("admin-teams"),
      });
      toast.success("Squad created");
      onCreated(team);
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error("Couldn't create squad", { description: formatErr(err) });
    },
  });

  return (
    <ConfirmDialog
      title="New squad"
      message={
        <div className="flex flex-col gap-2">
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
              Name
            </span>
            <input
              type="text"
              value={name}
              onChange={(e) => {
                const next = e.target.value;
                setName(next);
                // Slug auto-derive until user edits it manually.
                if (!slugEdited) setSlug(slugify(next));
              }}
              data-testid="admin-teams-new-name"
              className="rounded border border-rule bg-surface px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
              Slug
            </span>
            <input
              type="text"
              value={slug}
              onChange={(e) => {
                setSlugEdited(true);
                setSlug(e.target.value);
              }}
              data-testid="admin-teams-new-slug"
              className="rounded border border-rule bg-surface px-2 py-1.5 text-xs font-mono text-ink focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
              Default game format
            </span>
            <TkticSelect
              value={gameFormat}
              onChange={(next) => setGameFormat(next)}
              data-testid="admin-teams-new-format"
              size="sm"
              ariaLabel="Default game format"
              options={GAME_FORMATS.map((f) => ({ value: f, label: f }))}
            />
          </label>
          {error ? (
            <p
              data-testid="admin-teams-new-error"
              className="text-destructive font-mono text-xs"
            >
              {error}
            </p>
          ) : null}
        </div>
      }
      confirmLabel={mut.isPending ? "Creating…" : "Create squad"}
      onConfirm={() => {
        setError(null);
        if (!name.trim() || !slug.trim()) {
          setError("Name and slug are required.");
          return;
        }
        mut.mutate();
      }}
      onClose={onClose}
      data-testid="admin-teams-new-modal"
    />
  );
}

function EditTeamModal({
  team,
  onClose,
  onSaved,
}: {
  team: AdminTeamRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState(team.name);
  const [gameFormat, setGameFormat] = useState(team.game_format);
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      updateAdminTeam(team.id, {
        name: name !== team.name ? name : undefined,
        game_format: gameFormat !== team.game_format ? gameFormat : undefined,
      }),
    onSuccess: () => {
      toast.success("Squad updated");
      onSaved();
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error("Couldn't update squad", { description: formatErr(err) });
    },
  });

  const dirty = name !== team.name || gameFormat !== team.game_format;

  return (
    <ConfirmDialog
      title="Edit squad"
      message={
        <div className="flex flex-col gap-2">
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
              Name
            </span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="admin-teams-edit-name"
              className="rounded border border-rule bg-surface px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
              Default game format
            </span>
            <TkticSelect
              value={gameFormat}
              onChange={(next) => setGameFormat(next)}
              data-testid="admin-teams-edit-format"
              size="sm"
              ariaLabel="Default game format"
              options={GAME_FORMATS.map((f) => ({ value: f, label: f }))}
            />
          </label>
          <p className="text-[0.6rem] font-mono text-ink-muted">
            Slug is permanent ({team.slug}). Rename the squad or change
            its default format only.
          </p>
          {error ? (
            <p
              data-testid="admin-teams-edit-error"
              className="text-destructive font-mono text-xs"
            >
              {error}
            </p>
          ) : null}
        </div>
      }
      confirmLabel={mut.isPending ? "Saving…" : "Save"}
      onConfirm={() => {
        setError(null);
        if (!dirty) {
          onClose();
          return;
        }
        mut.mutate();
      }}
      onClose={onClose}
      data-testid="admin-teams-edit-modal"
    />
  );
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function formatErr(err: unknown): string {
  if (err instanceof ApiError) {
    return typeof err.detail === "string" ? err.detail : `HTTP ${err.status}`;
  }
  return "Request failed";
}
